import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase, capturedHashTokens } from "../lib/supabase";
import type { TeamMember, TeamRole } from "../lib/types";

interface AuthState {
  loading: boolean;
  session: Session | null;
  member: TeamMember | null;
  role: TeamRole | undefined;
  isSuperUser: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

const SUPER_USER_EMAILS = new Set<string>([
  "greg@bardinroofing.com",
  "chad@bardinroofing.com",
  "jennifer@bardinroofing.com",
  "jcohan@bardinroofing.com",
]);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [member, setMember] = useState<TeamMember | null>(null);
  const [resolved, setResolved] = useState(false);
  const bootstrapDone = useRef(false);

  // Bootstrap: establish session, THEN subscribe to changes.
  useEffect(() => {
    let mounted = true;

    // Helper: race a promise against a timeout
    function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
      return Promise.race([
        promise,
        new Promise<null>((resolve) => setTimeout(() => resolve(null), ms)),
      ]);
    }

    async function bootstrap() {
      let finalSession: Session | null = null;

      try {
        // 1. Normal session retrieval — with 6s timeout to prevent lock hangs
        const result = await withTimeout(supabase.auth.getSession(), 6000);
        finalSession = result?.data?.session ?? null;

        // 2. If no session but we captured hash tokens at module load, use them
        if (!finalSession && capturedHashTokens) {
          const setResult = await withTimeout(
            supabase.auth.setSession({
              access_token: capturedHashTokens.access_token,
              refresh_token: capturedHashTokens.refresh_token,
            }),
            5000,
          );
          if (setResult && !setResult.error) {
            finalSession = setResult.data.session;
          }
        }

        // 3. Handle PKCE ?code= param
        if (!finalSession) {
          const code = new URLSearchParams(window.location.search).get("code");
          if (code) {
            const exResult = await withTimeout(
              supabase.auth.exchangeCodeForSession(code),
              5000,
            );
            if (exResult && !exResult.error) {
              const d2 = await withTimeout(supabase.auth.getSession(), 3000);
              finalSession = d2?.data?.session ?? null;
            }
          }
        }
      } catch (_err) {
        // Bootstrap error — session stays null, user sees login screen
      }

      if (!mounted) return;

      // Clean up URL hash/params if we got a session from tokens
      if (finalSession && (window.location.hash.includes("access_token=") || window.location.search.includes("code="))) {
        window.history.replaceState(null, "", window.location.pathname);
      }

      // Set session state
      if (finalSession) {
        setSession(finalSession);
      } else {
        setResolved(true);
      }

      bootstrapDone.current = true;
    }

    bootstrap();

    // Safety net: if bootstrap is STILL hanging after 10s, force resolve
    // so the user at least sees the login screen instead of infinite loading.
    const safetyTimer = setTimeout(() => {
      if (!bootstrapDone.current && mounted) {
        bootstrapDone.current = true;
        setResolved(true);
      }
    }, 10000);

    // Subscribe to auth changes — but IGNORE events until bootstrap is done.
    // This prevents INITIAL_SESSION(null) from racing with our token processing.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      if (!bootstrapDone.current) return;
      setSession(s);
      if (!s) {
        setMember(null);
        setResolved(true);
      }
    });

    return () => {
      mounted = false;
      clearTimeout(safetyTimer);
      sub.subscription.unsubscribe();
    };
  }, []);

  // When session changes, look up the team_roster row by email.
  useEffect(() => {
    if (!session?.user?.email) {
      setMember(null);
      // If bootstrap finished and session has no email, resolve so login screen shows
      if (bootstrapDone.current) {
        setResolved(true);
      }
      return;
    }
    supabase
      .from("team_roster")
      .select("*")
      .eq("email", session.user.email)
      .eq("is_active", true)
      .maybeSingle()
      .then(({ data, error }) => {
        setMember((data as TeamMember) ?? null);
        setResolved(true);
        if (error) {
          console.warn("team_roster lookup failed:", error.message);
        }
      });
  }, [session]);

  // loading = true until bootstrap + member lookup have fully completed.
  // resolved is set to true only after: (a) no session found, or
  // (b) session found AND team_roster lookup finished.
  const loading = !resolved;

  const email = session?.user?.email?.toLowerCase() ?? "";
  const isSuperUser = member?.role === "admin" || SUPER_USER_EMAILS.has(email);

  const value: AuthState = {
    loading,
    session,
    member,
    role: member?.role,
    isSuperUser,
    signInWithGoogle: async () => {
      await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: window.location.origin + "/auth/callback",
          queryParams: { prompt: "select_account" },
        },
      });
    },
    signOut: async () => {
      await supabase.auth.signOut();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
